// Profil — ki vagyok, mi a hozzáférési szintem, mit tehetek.
//
// MIÉRT NINCS ÚJ VÉGPONT: a `GET /session` már MINDENT tartalmaz, amit ez az oldal mutat (név,
// e-mail, jog-lista, platform-admin jelző). Egy külön `/profile` végpont nemcsak felesleges
// lenne, hanem ÚJ TÁMADÁSI FELÜLET is: egy felhasználó-azonosítót fogadó profil-végpont a
// klasszikus IDOR-minta. Így a kérdés fel sem merül — az identitás a tokenből jön (`@Claims()`),
// a felület pedig ugyanazt a cache-elt session-lekérdezést használja, mint az app-váz.
//
// A JOG-LISTA NYERS KULCSOKKAL jelenik meg, csoportosítva. Nem lustaságból: a katalógus
// EMBER-OLVASHATÓ (kétnyelvű) címkéi az Odinban élnek (`GET /v1/admin/apps/morgens-hub/permissions`),
// és a `packages/shared/src/permissions.ts` kifejezetten tiltja egy második címke-szótár beírását
// — az két igazságforrás lenne, ami előbb-utóbb széttart. A csoportosítás viszont NEM szótár: a
// kulcsok szerkezete `<erőforrás>.<művelet>`, tehát a csoport magából a kulcsból következik.
import { roleFromPermissions } from '@morgens-hub/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { AppShell } from '@/components/shared/app-shell';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { displayName } from '@/lib/display-name';

export const Route = createFileRoute('/profil')({
  beforeLoad: ({ location }) => requireAuth(location.href),
  component: ProfilePage,
});

/**
 * A jog-kulcs erőforrás-része (`article.manage` → `article`). A `knowledge:read` scope-kulcs
 * kettősponttal tagol — azt is kezeljük, különben saját, értelmetlen csoportot kapna.
 */
function resourceOf(key: string): string {
  return key.split(/[.:]/)[0] ?? key;
}

/** Az ismert erőforrás-csoportok megjelenítési sorrendje; ami nem ismert, a végére kerül. */
const GROUP_ORDER = ['article', 'space', 'category', 'comment', 'revision', 'gap', 'knowledge'];

function groupPermissions(permissions: readonly string[]): [string, string[]][] {
  const groups = new Map<string, string[]>();
  for (const key of [...permissions].sort()) {
    const resource = resourceOf(key);
    groups.set(resource, [...(groups.get(resource) ?? []), key]);
  }
  return [...groups.entries()].sort(([a], [b]) => {
    const ia = GROUP_ORDER.indexOf(a);
    const ib = GROUP_ORDER.indexOf(b);
    return (ia === -1 ? GROUP_ORDER.length : ia) - (ib === -1 ? GROUP_ORDER.length : ib);
  });
}

function ProfilePage(): JSX.Element {
  const { t } = useTranslation();
  const session = api.session.useQuery(['session']);
  const body = session.data?.status === 200 ? session.data.body : null;

  const role = body ? roleFromPermissions(body.permissions) : null;
  const groups = body ? groupPermissions(body.permissions) : [];

  // A jogok EMBER-OLVASHATÓ nevei a Hub manifestjéből (BFF-végpont). Ha a katalógus nem érhető el,
  // a nyers kulcs marad — fail-open a MEGJELENÍTÉSBEN: a listát sosem tüntetjük el címke-hiba miatt.
  const catalog = api.permissionCatalog.useQuery(['permission-catalog'], undefined, {
    queryKey: ['permission-catalog'],
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const labelOf = (key: string): string =>
    catalog.data?.body.permissions.find((entry) => entry.key === key)?.label ?? key;

  return (
    <AppShell session={body}>
      <div className="mx-auto max-w-2xl">
        <header>
          <h1 className="text-read-2xl font-semibold tracking-tight">{t('profile.title')}</h1>
          <p className="text-muted-foreground text-read-sm mt-2">{t('profile.subtitle')}</p>
        </header>

        {!body && session.isLoading && <Skeleton className="mt-8 h-64 w-full" />}
        {!body && !session.isLoading && (
          <p className="text-destructive mt-8 text-sm">{t('profile.loadError')}</p>
        )}

        {body && (
          <div className="mt-8 space-y-8">
            <section aria-labelledby="profil-identitas">
              <h2 id="profil-identitas" className="text-sm font-semibold">
                {t('profile.identity')}
              </h2>
              <dl className="border-border bg-card mt-3 divide-y rounded-lg border">
                <div className="flex items-baseline gap-4 px-4 py-3">
                  <dt className="text-muted-foreground w-32 shrink-0 text-sm">
                    {t('profile.name')}
                  </dt>
                  <dd className="text-sm font-medium">
                    {displayName(body) || t('profile.nameMissing')}
                  </dd>
                </div>
                <div className="flex items-baseline gap-4 px-4 py-3">
                  <dt className="text-muted-foreground w-32 shrink-0 text-sm">
                    {t('profile.email')}
                  </dt>
                  <dd className="text-sm">{body.email ?? t('profile.emailMissing')}</dd>
                </div>
              </dl>
            </section>

            <section aria-labelledby="profil-szint">
              <h2 id="profil-szint" className="text-sm font-semibold">
                {t('profile.accessLevel')}
              </h2>
              {/* A szerep NEVE a jog-halmazból fejtődik vissza (a token csak a listát hordozza).
                  Ha a halmaz egyik tierrel sem egyezik PONTOSAN — mert a per-user grant/deny
                  eltérítette —, akkor „egyedi készlet" áll itt, nem egy közelítő tier-név: a
                  felület nem állíthat olyat a hozzáférésről, ami nem igaz. */}
              <p className="mt-3 text-sm">
                {role ? t(`profile.role.${role}`) : t('profile.role.custom')}
              </p>
              {body.isPlatformAdmin && (
                <p className="text-muted-foreground mt-2 text-sm">{t('profile.platformAdmin')}</p>
              )}
            </section>

            <section aria-labelledby="profil-jogok">
              <h2 id="profil-jogok" className="text-sm font-semibold">
                {t('profile.permissions')}
              </h2>
              {groups.length === 0 ? (
                <p className="text-muted-foreground mt-3 text-sm">{t('profile.permissionsEmpty')}</p>
              ) : (
                <div className="mt-3 space-y-4">
                  {groups.map(([resource, keys]) => (
                    <div key={resource}>
                      <h3 className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                        {/* Ismeretlen erőforrás (új katalógus-kulcs) esetén a nyers nevet írjuk ki:
                            így egy platform-bővítés nem tüntet el jogokat a listáról. */}
                        {t([`profile.group.${resource}`, 'profile.group.other'], {
                          resource,
                        })}
                      </h3>
                      <ul className="mt-1.5 flex flex-wrap gap-1.5">
                        {keys.map((key) => (
                          <li
                            key={key}
                            // A NÉV a katalógusból jön (a Hub manifestje), a nyers kulcs csak
                            // tooltipben marad: a hibabejelentéshez hasznos, a listában zaj.
                            title={key}
                            className="border-border bg-muted text-muted-foreground rounded-md border px-2 py-0.5 text-xs"
                          >
                            {labelOf(key)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
              {/* A jog-lista a token claimjéből jön, ami a szerep-kiosztás pillanatában készült:
                  egy frissen kapott jog a következő bejelentkezés/token-frissítés után jelenik meg. */}
              <p className="text-muted-foreground mt-4 text-xs">
                {t('profile.permissionsNote', { count: body.permissions.length })}
              </p>
            </section>
          </div>
        )}
      </div>
    </AppShell>
  );
}

